/**
 * VanguardPortfolioDashboard Component
 *
 * Main container component that provides a Vanguard-style tabbed interface
 * for viewing portfolio Holdings, Performance, and Insights.
 */

import React, { useState, useMemo } from 'react';
import { TrendingUp, BarChart3, Lightbulb, Plus } from 'lucide-react';
import { useCountry } from '../../CountryContext';
import { getCurrencyCode, getCurrencySymbol, formatCurrency as formatCurrencyConfig } from '../../countryConfig';
import { usePortfolioPrices } from '../../hooks/usePricing';

import VanguardHoldingsTable from './VanguardHoldingsTable';
import PerformanceDashboard from './PerformanceDashboard';
import InsightDashboard from './InsightDashboard';

const VanguardPortfolioDashboard = ({
  investments = [],
  accounts = [],
  cryptocurrencies = [],
  wealthSnapshots = [],
  liabilities = [],
  exchangeRates = null,
  isDark = false,
  onEditInvestment,
  onAddToPosition,
  onDeleteInvestment,
  onAddInvestment,
  onUpdateInvestmentSectorRegion,
  portfolioName = "My Portfolio"
}) => {
  const { selectedCountry } = useCountry();
  const displayCurrency = getCurrencyCode(selectedCountry);
  const currencySymbol = getCurrencySymbol(selectedCountry);

  const [activeTab, setActiveTab] = useState('holdings');

  // Fetch real-time prices for all investments
  const { prices, loading: pricesLoading, lastUpdated } = usePortfolioPrices(investments);

  // Calculate portfolio totals with live prices
  const portfolioTotals = useMemo(() => {
    let totalValue = 0;
    let totalCost = 0;
    let totalContributions = 0;

    investments.forEach(inv => {
      // Get live price data - check ticker first, then ISIN
      const priceData = inv.ticker ? prices[inv.ticker] : (inv.isin ? prices[inv.isin] : null);
      const hasLivePrice = priceData?.price && priceData.price > 0;
      const quantity = inv.quantity || 0;

      let currentValue = 0;
      let costBasis = 0;

      if (quantity > 0) {
        // Quantity-tracked investment
        const avgCost = inv.averageCostBasis || inv.pricePerUnit || 0;
        let currentPrice;

        if (hasLivePrice) {
          currentPrice = priceData.price;
        } else if (inv.pricePerUnit && inv.pricePerUnit > 0) {
          currentPrice = inv.pricePerUnit;
        } else {
          currentPrice = avgCost; // Fallback to avg cost (no gain/loss)
        }

        currentValue = quantity * currentPrice;
        costBasis = quantity * avgCost;
      } else {
        // Amount-only investment (legacy or simple tracking)
        currentValue = inv.amount || 0;
        costBasis = inv.totalCostBasis || inv.amount || 0;
      }

      // Convert to display currency if needed
      const invCurrency = inv.currency || displayCurrency;
      if (invCurrency !== displayCurrency && exchangeRates?.rates) {
        const fromRate = exchangeRates.rates[invCurrency] || 1;
        const toRate = exchangeRates.rates[displayCurrency] || 1;
        const conversionRate = toRate / fromRate;
        currentValue *= conversionRate;
        costBasis *= conversionRate;
      }

      totalValue += currentValue;
      totalCost += costBasis;
    });

    // Calculate contributions from snapshots
    wealthSnapshots.forEach(snapshot => {
      totalContributions += snapshot.changes?.newContributions || 0;
    });

    // If no snapshots, assume total cost is contributions
    if (totalContributions === 0) {
      totalContributions = totalCost;
    }

    const totalGainLoss = totalValue - totalCost;
    const gainLossPercent = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0;

    return {
      totalValue,
      totalCost,
      totalContributions,
      totalGainLoss,
      gainLossPercent,
      investmentReturns: totalValue - totalContributions
    };
  }, [investments, prices, displayCurrency, exchangeRates, wealthSnapshots]);

  const formatValue = (value) => {
    return formatCurrencyConfig(value, selectedCountry);
  };

  // Tab styling
  const tabClass = (tab) => `
    flex-1 sm:flex-none px-6 py-4 text-center font-semibold transition-all border-b-3
    ${activeTab === tab
      ? isDark
        ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800'
        : 'text-blue-600 border-b-2 border-blue-600 bg-white'
      : isDark
        ? 'text-gray-400 hover:text-gray-200 border-b-2 border-transparent hover:bg-gray-700/50'
        : 'text-gray-600 hover:text-gray-900 border-b-2 border-transparent hover:bg-gray-50'
    }
  `;

  return (
    <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Value as {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <h2 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatValue(portfolioTotals.totalValue)}
              </h2>
              <span className={`text-sm px-2 py-0.5 rounded ${
                portfolioTotals.totalGainLoss >= 0
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {portfolioTotals.totalGainLoss >= 0 ? '+' : ''}{portfolioTotals.gainLossPercent.toFixed(2)}%
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Add Investment Button */}
            <button
              onClick={() => onAddInvestment?.()}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isDark
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Investment</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <button onClick={() => setActiveTab('holdings')} className={tabClass('holdings')}>
          <span className="flex items-center justify-center gap-2">
            <BarChart3 className="w-4 h-4 hidden sm:inline" />
            Holdings
          </span>
        </button>
        <button onClick={() => setActiveTab('performance')} className={tabClass('performance')}>
          <span className="flex items-center justify-center gap-2">
            <TrendingUp className="w-4 h-4 hidden sm:inline" />
            Performance
          </span>
        </button>
        <button onClick={() => setActiveTab('insight')} className={tabClass('insight')}>
          <span className="flex items-center justify-center gap-2">
            <Lightbulb className="w-4 h-4 hidden sm:inline" />
            Insight
          </span>
        </button>
      </div>

      {/* Content */}
      <div className={`${activeTab === 'holdings' ? '' : 'p-6'}`}>
        {activeTab === 'holdings' && (
          <VanguardHoldingsTable
            investments={investments}
            accounts={accounts}
            totalPortfolioValue={portfolioTotals.totalValue}
            exchangeRates={exchangeRates}
            isDark={isDark}
            onEditInvestment={onEditInvestment}
            onAddToPosition={onAddToPosition}
            onDeleteInvestment={onDeleteInvestment}
            onUpdateInvestmentSectorRegion={onUpdateInvestmentSectorRegion}
          />
        )}

        {activeTab === 'performance' && (
          <PerformanceDashboard
            investments={investments}
            wealthSnapshots={wealthSnapshots}
            totalValue={portfolioTotals.totalValue}
            totalCost={portfolioTotals.totalCost}
            totalContributions={portfolioTotals.totalContributions}
            investmentReturns={portfolioTotals.investmentReturns}
            isDark={isDark}
            exchangeRates={exchangeRates}
          />
        )}

        {activeTab === 'insight' && (
          <InsightDashboard
            investments={investments}
            totalValue={portfolioTotals.totalValue}
            totalContributions={portfolioTotals.totalContributions}
            isDark={isDark}
            wealthSnapshots={wealthSnapshots}
          />
        )}
      </div>

    </div>
  );
};

export default VanguardPortfolioDashboard;
